/**
 * BuildLogsViewer — composant réutilisable pour afficher l'historique des builds
 * d'un moteur analytique (Kaniko / registry).
 *
 * Usage :
 *   <BuildLogsViewer open={open} modelId={modelId} onClose={() => setOpen(false)} showToast={showToast}/>
 */
import React from "react";
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, Divider, IconButton, Paper, Stack, Table, TableBody,
    TableCell, TableRow, Tooltip, Typography,
} from "@mui/material";
import BuildIcon        from "@mui/icons-material/Build";
import CheckCircleIcon  from "@mui/icons-material/CheckCircle";
import ErrorIcon        from "@mui/icons-material/Error";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ExpandMoreIcon   from "@mui/icons-material/ExpandMore";
import AccessTimeIcon   from "@mui/icons-material/AccessTime";
import ContentCopyIcon  from "@mui/icons-material/ContentCopy";
import LiveTvIcon       from "@mui/icons-material/LiveTv";
import RefreshIcon      from "@mui/icons-material/Refresh";
import ApiAnalyticsEngineCtrl from "../../../../api/ApiAnalyticsEngineCtrl";
import AnsiText from "../../../../components/AnsiText";

// ── Helpers ────────────────────────────────────────────────────────────────────

function buildStatusColor(status) {
    if (!status) return "default";
    const s = status.toUpperCase();
    if (["SUCCESS", "COMPLETED", "OK"].includes(s))         return "success";
    if (["FAILED", "ERROR", "FAILURE"].includes(s))         return "error";
    if (["RUNNING", "BUILDING", "IN_PROGRESS"].includes(s)) return "info";
    if (["PENDING", "QUEUED"].includes(s))                  return "warning";
    return "default";
}

function buildStatusIcon(status) {
    if (!status) return <WarningAmberIcon fontSize="small" color="disabled"/>;
    const s = status.toUpperCase();
    if (["SUCCESS", "COMPLETED", "OK"].includes(s))         return <CheckCircleIcon fontSize="small" color="success"/>;
    if (["FAILED", "ERROR", "FAILURE"].includes(s))         return <ErrorIcon fontSize="small" color="error"/>;
    if (["RUNNING", "BUILDING", "IN_PROGRESS"].includes(s)) return <BuildIcon fontSize="small" color="info"/>;
    return <WarningAmberIcon fontSize="small" color="warning"/>;
}

function formatDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "medium" }); }
    catch { return iso; }
}

function buildDuration(start, end) {
    if (!start || !end) return null;
    const ms = new Date(end) - new Date(start);
    if (isNaN(ms) || ms < 0) return null;
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    return `${m}m ${s % 60}s`;
}

const LOG_STYLE = {
    display: "block", px: 2, py: 1.5, m: 0,
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace",
    fontSize: 11.5, lineHeight: 1.65,
    whiteSpace: "pre-wrap", wordBreak: "break-all",
    overflow: "auto",
    bgcolor: "#0d1117", color: "#c9d1d9",
};

// ── Détail d'un build ──────────────────────────────────────────────────────────

function BuildLogDetail({ build, onCopy }) {
    const logRef     = React.useRef(null);
    const initLogRef = React.useRef(null);

    React.useEffect(() => {
        if (logRef.current)     logRef.current.scrollTop     = logRef.current.scrollHeight;
        if (initLogRef.current) initLogRef.current.scrollTop = initLogRef.current.scrollHeight;
    }, [build]);

    return (
        <Box>
            {/* Méta */}
            dsqdsqdqs

            <Table size="small" sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
                <TableBody>
                    {[
                        ["ID",         build.id],
                        ["Image",      build.image],
                        ["Démarré le", formatDate(build.startedAt)],
                        ["Terminé le", formatDate(build.finishedAt)],
                        ["Durée",      buildDuration(build.startedAt, build.finishedAt) ?? "—"],
                    ].map(([label, value]) => (
                        <TableRow key={label}>
                            <TableCell sx={{ fontWeight: 600, fontSize: 11, color: "text.secondary", width: 140, py: 0.75 }}>{label}</TableCell>
                            <TableCell sx={{ fontSize: 11, fontFamily: "monospace", py: 0.75 }}>{value ?? "—"}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {/* Init logs */}
            {build.initLogs && (
                <>
                    <Stack direction="row" alignItems="center" gap={1} px={2} py={0.75}
                           sx={{ bgcolor: "#161b22", borderBottom: "1px solid #30363d" }}>
                        <Typography variant="caption" fontWeight={700} color="#79c0ff"
                                    sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                            Init logs
                        </Typography>
                    </Stack>
                    <AnsiText ref={initLogRef}
                              sx={{ ...LOG_STYLE, maxHeight: 180, color: "#79c0ff" }}>
                        {build.initLogs}
                    </AnsiText>
                </>
            )}

            {/* Build logs */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" px={2} py={0.75}
                   sx={{
                       bgcolor: "#161b22",
                       borderBottom: "1px solid #30363d",
                       borderTop: build.initLogs ? "1px solid #30363d" : "none",
                   }}>
                <Typography variant="caption" fontWeight={700} color="#f0883e"
                            sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Build logs
                </Typography>
                <Tooltip title="Copier les logs">
                    <IconButton size="small" onClick={() => onCopy(build)}
                                sx={{ color: "#8b949e", "&:hover": { color: "#c9d1d9" } }}>
                        <ContentCopyIcon sx={{ fontSize: 14 }}/>
                    </IconButton>
                </Tooltip>
            </Stack>
            <AnsiText ref={logRef} sx={{ ...LOG_STYLE, maxHeight: 420 }}
                      placeholder="(pas de logs de build)">
                {build.logs}
            </AnsiText>
        </Box>
    );
}

// ── Logs en direct ─────────────────────────────────────────────────────────────

function LiveLogsPanel({ modelId, showToast }) {
    const [logs,    setLogs]    = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const logRef = React.useRef(null);

    const fetchLive = React.useCallback(async () => {
        setLoading(true);
        const { data, error } = await ApiAnalyticsEngineCtrl.getLiveBuildLogs(modelId);
        if (data != null) {
            setLogs(data);
            setTimeout(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, 50);
        }
        if (error) showToast({ msg: "Erreur chargement des logs live", severity: "error" });
        setLoading(false);
    }, [modelId, showToast]);

    React.useEffect(() => { fetchLive(); }, [fetchLive]);

    return (
        <Box sx={{ borderRadius: 1.5, overflow: "hidden", border: "1px solid", borderColor: "divider" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" px={2} py={0.75}
                   sx={{ bgcolor: "#161b22", borderBottom: "1px solid #30363d" }}>
                <Stack direction="row" alignItems="center" gap={1}>
                    <LiveTvIcon sx={{ fontSize: 14, color: "#79c0ff" }}/>
                    <Typography variant="caption" fontWeight={700} color="#79c0ff"
                                sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Logs en direct
                    </Typography>
                    {loading && <CircularProgress size={10} sx={{ color: "#79c0ff" }}/>}
                </Stack>
                <Tooltip title="Rafraîchir">
                    <IconButton size="small" onClick={fetchLive} disabled={loading}
                                sx={{ color: "#8b949e", "&:hover": { color: "#c9d1d9" } }}>
                        <RefreshIcon sx={{ fontSize: 14 }}/>
                    </IconButton>
                </Tooltip>
            </Stack>
            <AnsiText ref={logRef} sx={{ ...LOG_STYLE, maxHeight: 500 }}
                      placeholder={loading ? "Chargement…" : "(aucun log en cours)"}>
                {logs}
            </AnsiText>
        </Box>
    );
}

// ── Liste des builds ───────────────────────────────────────────────────────────

function BuildLogsList({ modelId, showToast }) {
    const [builds,          setBuilds]          = React.useState([]);
    const [loading,         setLoading]         = React.useState(true);
    const [selectedBuildId, setSelectedBuildId] = React.useState(null);

    const load = React.useCallback(async () => {
        setLoading(true);
        const { data, error } = await ApiAnalyticsEngineCtrl.getBuildLogs(modelId);
        if (data)  setBuilds(data.slice().reverse());
        if (error) showToast({ msg: "Erreur chargement des logs de build", severity: "error" });
        setLoading(false);
    }, [modelId, showToast]);

    React.useEffect(() => { load(); }, [load]);

    // Ouvre automatiquement le build le plus récent
    React.useEffect(() => {
        if (builds.length > 0 && selectedBuildId === null)
            setSelectedBuildId(builds[0].id ?? 0);
    }, [builds, selectedBuildId]);

    async function handleCopy(build) {
        const lines = [];
        if (build.initLogs) lines.push("=== INIT LOGS ===\n" + build.initLogs);
        lines.push("=== BUILD LOGS ===\n" + (build.logs ?? "(pas de logs)"));
        try {
            await navigator.clipboard.writeText(lines.join("\n\n"));
            showToast({ msg: "Logs copiés dans le presse-papier", severity: "success" });
        } catch {
            showToast({ msg: "Impossible d'accéder au presse-papier", severity: "error" });
        }
    }

    if (loading && builds.length === 0)
        return <Stack alignItems="center" py={6}><CircularProgress/></Stack>;

    if (builds.length === 0)
        return (
            <Stack alignItems="center" py={8} gap={1}>
                <BuildIcon sx={{ fontSize: 48, color: "text.disabled" }}/>
                <Typography color="text.secondary">Aucun build disponible pour ce moteur</Typography>
            </Stack>
        );

    return (
        <Stack spacing={1}>
            {builds.map((build, i) => {
                const key        = build.id ?? i;
                const isSelected = selectedBuildId === key;
                const dur        = buildDuration(build.startedAt, build.finishedAt);

                return (
                    <Paper key={key} elevation={0} variant="outlined"
                           sx={{
                               borderRadius: 2, overflow: "hidden",
                               borderColor: isSelected ? "primary.main" : "divider",
                               borderWidth: isSelected ? 2 : 1,
                           }}>
                        {/* Ligne résumé — cliquable */}
                        <Stack direction="row" alignItems="center" gap={1.5} px={2} py={1.25}
                               onClick={() => setSelectedBuildId(isSelected ? null : key)}
                               sx={{
                                   cursor: "pointer",
                                   "&:hover": { bgcolor: "action.hover" },
                                   bgcolor: isSelected ? "action.selected" : "transparent",
                               }}>
                            {buildStatusIcon(build.status)}

                            <Chip label={build.status ?? "?"} size="small"
                                  color={buildStatusColor(build.status)} variant="filled"
                                  sx={{ fontWeight: 700, fontSize: 11 }}/>

                            <Typography variant="body2" fontFamily="monospace" fontSize={12}
                                        flex={1} noWrap color="text.primary">
                                {build.image ?? build.id ?? `Build #${i + 1}`}
                            </Typography>

                            <Stack direction="row" gap={1.5} alignItems="center" flexShrink={0}>
                                {dur && (
                                    <Stack direction="row" alignItems="center" gap={0.5}>
                                        <AccessTimeIcon sx={{ fontSize: 13, color: "text.disabled" }}/>
                                        <Typography variant="caption" color="text.secondary">{dur}</Typography>
                                    </Stack>
                                )}
                                <Typography variant="caption" color="text.secondary"
                                            sx={{ display: { xs: "none", sm: "block" } }}>
                                    {formatDate(build.startedAt)}
                                </Typography>
                                <ExpandMoreIcon fontSize="small" color="action"
                                               sx={{ transition: "transform .2s",
                                                     transform: isSelected ? "rotate(180deg)" : "none" }}/>
                            </Stack>
                        </Stack>

                        {/* Détail déroulant */}
                        {isSelected && (
                            <Box sx={{ borderTop: "1px solid", borderColor: "divider" }}>
                                <BuildLogDetail build={build} onCopy={handleCopy}/>
                            </Box>
                        )}
                    </Paper>
                );
            })}
        </Stack>
    );
}

// ── Dialog principal exporté ───────────────────────────────────────────────────

export default function BuildLogsViewer({ open, modelId, onClose, showToast }) {
    const [key,      setKey]      = React.useState(0);
    const [showLive, setShowLive] = React.useState(false);

    // Recharge la liste à chaque ouverture
    React.useEffect(() => {
        if (open) { setKey((k) => k + 1); setShowLive(false); }
    }, [open]);

    if (!modelId) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
                slotProps={{ paper: { sx: { borderRadius: 3 } } }}>
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" alignItems="center" gap={1.5}>
                    <BuildIcon color="primary"/>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                            Logs de build
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                            {modelId}
                        </Typography>
                    </Box>
                    {/* Toggle logs live */}
                    <Tooltip title={showLive ? "Voir l'historique" : "Voir les logs en direct"}>
                        <Button
                            size="small" variant={showLive ? "contained" : "outlined"}
                            color="info" startIcon={<LiveTvIcon fontSize="small"/>}
                            onClick={() => setShowLive(v => !v)}>
                            Live
                        </Button>
                    </Tooltip>
                </Stack>
            </DialogTitle>
            <Divider/>
            <DialogContent sx={{ pt: 2 }}>
                {showLive
                    ? <LiveLogsPanel key={`live-${key}`} modelId={modelId} showToast={showToast}/>
                    : <BuildLogsList key={key} modelId={modelId} showToast={showToast}/>
                }
            </DialogContent>
            <Divider/>
            <DialogActions sx={{ px: 3, py: 1.5 }}>
                <Button onClick={onClose}>Fermer</Button>
            </DialogActions>
        </Dialog>
    );
}

